/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDistributionPluginsRequest,
  ListDistributionPluginsSecurity,
} from "../models/operations/listdistributionplugins.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDistributionPluginsQuery,
  DistributionPluginsQueryData,
  prefetchDistributionPlugins,
  queryKeyDistributionPlugins,
} from "./distributionPlugins.core.js";
export {
  buildDistributionPluginsQuery,
  type DistributionPluginsQueryData,
  prefetchDistributionPlugins,
  queryKeyDistributionPlugins,
};

export type DistributionPluginsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDistributionPlugins plugins
 *
 * @remarks
 * List minimal distribution targets for a skill the caller can read. Requires skill:read, not org:read.
 */
export function useDistributionPlugins(
  request: ListDistributionPluginsRequest,
  security?: ListDistributionPluginsSecurity | undefined,
  options?: QueryHookOptions<
    DistributionPluginsQueryData,
    DistributionPluginsQueryError
  >,
): UseQueryResult<DistributionPluginsQueryData, DistributionPluginsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDistributionPluginsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDistributionPlugins plugins
 *
 * @remarks
 * List minimal distribution targets for a skill the caller can read. Requires skill:read, not org:read.
 */
export function useDistributionPluginsSuspense(
  request: ListDistributionPluginsRequest,
  security?: ListDistributionPluginsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DistributionPluginsQueryData,
    DistributionPluginsQueryError
  >,
): UseSuspenseQueryResult<
  DistributionPluginsQueryData,
  DistributionPluginsQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDistributionPluginsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDistributionPluginsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      skillId: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DistributionPluginsQueryData,
): DistributionPluginsQueryData | undefined {
  const key = queryKeyDistributionPlugins(...queryKeyBase);

  return client.setQueryData<DistributionPluginsQueryData>(key, data);
}

export function invalidateDistributionPlugins(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      skillId: string;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "plugins",
      "listDistributionPlugins",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllDistributionPlugins(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "plugins", "listDistributionPlugins"],
  });
}
